import { useState, useEffect, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { useSearchParams } from "react-router-dom";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Spinner, Input, Button } from "@heroui/react";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Scribble } from "shared/types/scribble";
import { TagSidebar } from "./components/tag-sidebar";
import { NotesList } from "./components/notes-list";
import { NoteEditorSimple } from "./components/note-editor-simple";
import { DeleteConfirmationModal } from "./components/delete-confirmation-modal";
import { toast } from "react-toastify";

const NotesOverviewSimple = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notes, setNotes] = useState<Scribble[]>([]);
  const [trashNotes, setTrashNotes] = useState<Scribble[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<Scribble[]>([]);
  const [selectedNote, setSelectedNote] = useState<Scribble | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [trashCount, setTrashCount] = useState(0);
  const [trashLoaded, setTrashLoaded] = useState(false);
  const { session } = useSession();

  // New state for company-wide and building/reno filtering
  const [showCompanyWide, setShowCompanyWide] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [selectedRenovation, setSelectedRenovation] = useState<number | null>(null);
  const [showTrash, setShowTrash] = useState(false);

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<Scribble | null>(null);

  // Filter notes based on trash view
  const activeNotes = useMemo(() => {
    return showTrash ? trashNotes : notes;
  }, [notes, trashNotes, showTrash]);

  // Extract unique buildings from notes (excluding soft-deleted unless in trash view)
  const buildings = useMemo(() => {
    const buildingMap = new Map<string, number>();
    activeNotes.forEach(note => {
      if (note.building?.id) {
        buildingMap.set(
          note.building.id,
          (buildingMap.get(note.building.id) || 0) + 1
        );
      }
    });
    return Array.from(buildingMap.entries())
      .map(([id, count]) => ({ id, count }))
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [activeNotes]);

  // Extract unique renovation projects from notes (excluding soft-deleted unless in trash view)
  const renovationProjects = useMemo(() => {
    const projectMap = new Map<number, { title: string; count: number }>();
    activeNotes.forEach(note => {
      if (note.renovationProject) {
        const existing = projectMap.get(note.renovationProject.id);
        projectMap.set(note.renovationProject.id, {
          title: note.renovationProject.title,
          count: (existing?.count || 0) + 1
        });
      }
    });
    return Array.from(projectMap.entries())
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => a.id - b.id);
  }, [activeNotes]);

  // Extract tags with counts from notes (excluding soft-deleted unless in trash view)
  const tagsWithCounts = useMemo(() => {
    const tagMap = new Map<string, number>();
    activeNotes.forEach(note => {
      if (note.tags) {
        const noteTags = note.tags.split(',').map(t => t.trim()).filter(Boolean);
        noteTags.forEach(tag => {
          tagMap.set(tag, (tagMap.get(tag) || 0) + 1);
        });
      }
    });
    return Array.from(tagMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [activeNotes]);

  // Fetch notes and trash count on mount and when company-wide toggle changes
  useEffect(() => {
    if (session?.user?.id) {
      fetchNotes();
      fetchTrashCount();
    }
  }, [session?.user?.id, showCompanyWide]);

  // Fetch trash notes when opening trash view
  useEffect(() => {
    if (showTrash && session?.user?.id && !trashLoaded) {
      fetchTrashNotes();
    }
  }, [showTrash, session?.user?.id]);

  // Handle noteId from query params
  useEffect(() => {
    const noteId = searchParams.get('noteId');
    if (noteId && notes.length > 0) {
      const note = notes.find(n => n.id === parseInt(noteId));
      if (note) {
        console.log('Opening note from URL:', note);
        setSelectedNote(note);
      }
    }
  }, [searchParams, notes]);

  // Handle create note with pre-linked resources from query params
  useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    const renovationProjectId = searchParams.get('renovationProjectId');
    const buildingId = searchParams.get('buildingId');

    if (shouldCreate && (renovationProjectId || buildingId)) {
      // Fetch the linked resources and create a new note with pre-populated data
      const fetchAndCreateNote = async () => {
        try {
          const newNote: Partial<Scribble> = {
            title: '',
            content: '',
            userId: session?.user?.id,
          };

          // Fetch building if provided
          if (buildingId) {
            const buildingResponse = await fetchWithAuth(
              `${API_URL}/building/${buildingId}`,
              { method: 'GET' },
              session
            );
            if (buildingResponse.ok) {
              const building = await buildingResponse.json();
              newNote.building = building;
            }
          }

          // Fetch renovation project if provided
          if (renovationProjectId) {
            const projectResponse = await fetchWithAuth(
              `${API_URL}/renovation-project/${renovationProjectId}`,
              { method: 'GET' },
              session
            );
            if (projectResponse.ok) {
              const project = await projectResponse.json();
              newNote.renovationProject = project;
            }
          }

          // Set the note with pre-populated resources
          setSelectedNote(newNote as Scribble);

          // Clear the query params to avoid re-triggering
          setSearchParams({});
        } catch (error) {
          console.error('Error fetching linked resources:', error);
          toast.error('Fout bij laden van gekoppelde resources');
        }
      };

      if (session?.user?.id) {
        fetchAndCreateNote();
      }
    }
  }, [searchParams, session?.user?.id]);

  // Filter notes when search term, tag, building, or renovation project changes
  useEffect(() => {
    let filtered = activeNotes;

    // Filter by tag
    if (selectedTag) {
      filtered = filtered.filter(note =>
        note.tags?.split(',').map(t => t.trim()).includes(selectedTag)
      );
    }

    // Filter by building
    if (selectedBuilding) {
      filtered = filtered.filter(note => note.building?.id === selectedBuilding);
    }

    // Filter by renovation project
    if (selectedRenovation) {
      filtered = filtered.filter(note => note.renovationProject?.id === selectedRenovation);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(note =>
        note.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.content?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredNotes(filtered);
  }, [activeNotes, selectedTag, selectedBuilding, selectedRenovation, searchTerm]);

  const fetchNotes = async () => {
    try {
      setIsLoading(true);

      // Use search endpoint with includeGlobal for company-wide, or regular endpoint for user-specific
      // Backend now automatically excludes soft-deleted notes
      const endpoint = showCompanyWide
        ? `${API_URL}/scribble/search?userId=${session?.user?.id}&includeGlobal=true`
        : `${API_URL}/scribble?userId=${session?.user?.id}`;

      const response = await fetchWithAuth(
        endpoint,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to fetch notes: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      setNotes(data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Fout bij ophalen van notities');
      throw error; // Re-throw for Sentry to capture
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTrashCount = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/scribble/trash-count?userId=${session?.user?.id}`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setTrashCount(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching trash count:', error);
    }
  };

  const fetchTrashNotes = async () => {
    if (trashLoaded) return; // Only fetch once

    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/scribble/trash?userId=${session?.user?.id}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Failed to fetch trash notes: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      setTrashNotes(data);
      setTrashLoaded(true);
    } catch (error) {
      console.error('Error fetching trash notes:', error);
      toast.error('Fout bij ophalen van prullenbak');
    } finally {
      setIsLoading(false);
    }
  };


  const handleCreateNote = () => {
    // Clear any noteId query parameter to prevent returning to old note after save
    setSearchParams({});
    setSelectedNote(null);
    // Editor will show empty form for new note
  };

  const handleSelectNote = (note: Scribble) => {
    setSelectedNote(note);
  };

  const handleSaveNote = async (noteData: Partial<Scribble>) => {
    try {
      if (noteData.id) {
        // Update existing note
        const response = await fetchWithAuth(
          `${API_URL}/scribble/${noteData.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(noteData)
          },
          session
        );

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`Failed to update note ${noteData.id}: ${response.status} ${errorText}`);
        }

        const updatedNote = await response.json();

        // Update in list
        setNotes(prev => prev.map(n => n.id === updatedNote.id ? updatedNote : n));

        // Only update selectedNote if this is still the currently selected note
        // This prevents background saves from overriding user's note selection
        setSelectedNote(prev => prev?.id === updatedNote.id ? updatedNote : prev);
        // Icon indicator in header is enough, no toast needed
      } else {
        // Create new note
        const response = await fetchWithAuth(
          `${API_URL}/scribble`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...noteData,
              userId: session?.user?.id
            })
          },
          session
        );

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          throw new Error(`Failed to create note: ${response.status} ${errorText}`);
        }

        const newNote = await response.json();

        // Add to list and select (only for new notes, not background saves)
        setNotes(prev => [newNote, ...prev]);

        // Only select if user is not already on another note
        setSelectedNote(prev => prev ? prev : newNote);
        // Icon indicator in header is enough, no toast needed
      }

      // Tags will automatically update via useMemo when notes change
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Fout bij opslaan van notitie');
      throw error; // Re-throw for Sentry to capture
    }
  };

  const handleDeleteNote = async (note: Scribble) => {
    setNoteToDelete(note);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!noteToDelete) return;

    try {
      // Soft delete: update the note with softDelete = true
      const response = await fetchWithAuth(
        `${API_URL}/scribble/${noteToDelete.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            softDelete: true
          })
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to delete note');
      }

      // Remove from active notes list
      setNotes(prev => prev.filter(n => n.id !== noteToDelete.id));

      // Update trash count instantly
      setTrashCount(prev => prev + 1);

      // Reset trash loaded flag to force reload when opening trash
      setTrashLoaded(false);

      // Clear selection if deleted note was selected
      if (selectedNote?.id === noteToDelete.id) {
        setSelectedNote(null);
      }

      toast.success('Notitie verplaatst naar prullenbak');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Fout bij verwijderen van notitie');
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Notities &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="flex h-screen w-full bg-gradient-to-br from-white to-yally-fine/20">
        {/* Sidebar with tags */}
        <div className="hidden lg:block w-44 border-r border-neutral-primary/10 bg-white/50 backdrop-blur-sm">
          <TagSidebar
            tags={tagsWithCounts}
            selectedTag={selectedTag}
            onSelectTag={setSelectedTag}
            notesCount={activeNotes.length}
            showCompanyWide={showCompanyWide}
            onShowCompanyWideChange={setShowCompanyWide}
            buildings={buildings}
            selectedBuilding={selectedBuilding}
            onSelectBuilding={setSelectedBuilding}
            renovationProjects={renovationProjects}
            selectedRenovation={selectedRenovation}
            onSelectRenovation={setSelectedRenovation}
            showTrash={showTrash}
            onShowTrashChange={setShowTrash}
            trashCount={trashCount}
          />
        </div>

        {/* Notes list */}
        <div className="w-56 md:w-60 border-r border-neutral-primary/10 flex flex-col bg-white/60 backdrop-blur-sm">
          <div className="px-3 py-2 border-b border-neutral-primary/5 flex items-center justify-between">
            <Input
              type="text"
              placeholder="Zoek notities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startContent={<MagnifyingGlass weight="duotone" size={16} />}
              size="sm"
              classNames={{
                base: "flex-1",
                inputWrapper: "bg-neutral-primary/5 border-none shadow-none h-8"
              }}
            />
            <Button
              size="sm"
              color="primary"
              className="bg-moss-primary text-white ml-2"
              isIconOnly
              onPress={handleCreateNote}
            >
              <Plus weight="bold" size={16} />
            </Button>
          </div>

          <div className="flex-1 overflow-hidden">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Spinner size="md" color="warning" />
              </div>
            ) : (
              <NotesList
                notes={filteredNotes}
                selectedNote={selectedNote}
                onSelectNote={handleSelectNote}
              />
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1">
          <NoteEditorSimple
            note={selectedNote}
            onSave={handleSaveNote}
            onDelete={handleDeleteNote}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setNoteToDelete(null);
        }}
        onConfirm={confirmDelete}
        noteTitle={noteToDelete?.title}
      />
    </>
  );
};

export default NotesOverviewSimple;
